import { useMemo, useEffect, useState, useRef } from 'react';
import { ResourceType } from 'libs.library_manager';
import { AssetTypes } from 'libs.localization.common';
import { useLocalization } from 'libs.localization.react';
import { useActivityDetails } from './use_activity_details.hook';
import { useLibraryDetails } from './use_library_details.hook';
import { ActivityOrigin } from '../types';
/**
 * Hook to determine activity source status and fetch source activity details.
 *
 * This hook analyzes an activity to determine its origin (original, cloned, versioned, or cloned from another workspace)
 * and fetches the corresponding source activity information if applicable.
 *
 * @param activityId - The activity ID to check. If undefined, returns empty state.
 * @returns Object containing source activity status information including origin, source activity details, and loading states.
 *
 * @example
 * ```tsx
 * import { useActivitySourceStatus } from 'libs.ecoa_shared';
 *
 * const MyComponent = ({ activityId }) => {
 *   const {
 *     activityOrigin,
 *     sourceActivityName,
 *     sourceActivityVersion,
 *     isLoadingSourceActivity
 *   } = useActivitySourceStatus(activityId);
 *
 *   if (isLoadingSourceActivity) {
 *     return <Spinner />;
 *   }
 *
 *   return (
 *     <div>
 *       <p>Origin: {activityOrigin}</p>
 *       {sourceActivityName && <p>Source: {sourceActivityName}</p>}
 *     </div>
 *   );
 * };
 * ```
 *
 * @public
 */
export const useActivitySourceStatus = (activityId) => {
    // Get current activity details using useLibraryDetails
    const { data: activityResponse, isLoading: isLoadingActivity } = useLibraryDetails(activityId, ResourceType.ACTIVITY);
    // Extract current activity data from the correct path
    const activityResource = activityResponse?.data?.data;
    const activity = activityResource?.data;
    const currentVersion = parseInt(activityResource?.version || '1', 10);
    const currentKey = activityResource?.key;
    // Extract clone metadata for workspace information (cloned from another workspace)
    const cloneMetadata = activityResource?.metadata?.clone;
    // Get source_id from activity metadata (original clone detection logic)
    // Prefer clone metadata sourceId if available (for cloned from another workspace)
    const sourceId = cloneMetadata?.sourceId || activityResource?.metadata?.source_id;
    const sourceLink = sourceId ? `/library/activities/${sourceId}` : undefined;
    const sourceWorkspaceId = cloneMetadata?.sourceEntity?.id;
    const sourceWorkspaceName = cloneMetadata?.sourceEntity?.name;
    // Determine if this is a cloned activity (source_id exists and version is 1) or versioned activity (version > 1)
    const isCloned = sourceId && currentVersion === 1;
    const isCrossWorkspaceCloned = cloneMetadata?.cloned;
    // For cloned activities, use useLibraryDetails with sourceId
    const { data: clonedSourceResponse, isLoading: isLoadingClonedSource } = useLibraryDetails(isCrossWorkspaceCloned || isCloned ? sourceId : undefined, ResourceType.ACTIVITY);
    // For versioned activities, calculate source activity parameters
    const sourceActivityParams = useMemo(() => {
        // Only fetch source activity if we have a sourceId and currentKey, and it's not a clone
        if (!sourceId || !currentKey || isCrossWorkspaceCloned || isCloned) {
            return undefined;
        }
        // For versioned activities: version=currentVersion-1 and active=false
        const sourceVersion = Math.max(1, currentVersion - 1);
        return {
            key: currentKey,
            version: sourceVersion,
            active: false,
        };
    }, [sourceId, currentKey, currentVersion, isCrossWorkspaceCloned, isCloned]);
    // Fetch source activity using the calculated parameters for versioned activities
    const { data: versionedSourceResponse, isLoading: isLoadingVersionedSource } = useActivityDetails(sourceActivityParams);
    // Determine which response to use based on activity type
    const sourceActivityResponse = isCrossWorkspaceCloned || isCloned ? clonedSourceResponse : versionedSourceResponse;
    const isLoadingSourceActivity = isCrossWorkspaceCloned || isCloned ? isLoadingClonedSource : isLoadingVersionedSource;
    // Extract source activity data - handle both response formats
    const sourceActivity = isCrossWorkspaceCloned || isCloned
        ? sourceActivityResponse?.data?.data?.data
        : sourceActivityResponse?.data?.[0]?.data;
    const sourceActivityName = useMemo(() => sourceActivity?.short_name, [sourceActivity?.short_name]);
    const sourceActivityVersion = useMemo(() => {
        // For cloned from another workspace, use clone metadata sourceVersion if source activity can't be fetched
        if ((isCrossWorkspaceCloned || isCloned) && sourceWorkspaceId && !sourceActivity) {
            return cloneMetadata?.sourceVersion?.toString();
        }
        if (isCrossWorkspaceCloned || isCloned) {
            return sourceActivityResponse?.data?.data?.version;
        }
        return sourceActivityResponse?.data?.[0]?.version;
    }, [
        isCrossWorkspaceCloned,
        isCloned,
        sourceWorkspaceId,
        sourceActivity,
        cloneMetadata?.sourceVersion,
        sourceActivityResponse?.data?.data?.version,
        sourceActivityResponse?.data?.[0]?.version,
    ]);
    // Handle localization data asynchronously - only when sourceActivity is available
    const sourceAsset = useMemo(() => {
        return sourceActivity?.identifier
            ? {
                type: AssetTypes.ACTIVITY,
                id: sourceActivity.identifier,
                version: '1.0',
            }
            : undefined;
    }, [sourceActivity?.identifier]);
    const { localization: sourceLocalization } = useLocalization(sourceAsset);
    // State for supported locales
    const [supportedLocales, setSupportedLocales] = useState(undefined);
    const [isLoadingSupportedLocales, setIsLoadingSupportedLocales] = useState(false);
    const previousSourceAssetId = useRef(undefined);
    // Fetch supported locales asynchronously
    useEffect(() => {
        const currentSourceAssetId = sourceAsset?.id;
        // Only fetch if the source asset ID has actually changed
        if (currentSourceAssetId === previousSourceAssetId.current) {
            return;
        }
        previousSourceAssetId.current = currentSourceAssetId;
        const fetchSupportedLocales = async () => {
            if (!sourceLocalization) {
                setSupportedLocales(undefined);
                setIsLoadingSupportedLocales(false);
                return;
            }
            setIsLoadingSupportedLocales(true);
            try {
                const sourceDbEntry = await sourceLocalization.getDatabaseEntry();
                const allLocales = sourceDbEntry?.supported_locales ? Object.keys(sourceDbEntry.supported_locales) : [];
                // Filter out en-US (base locale) from supported locales
                const locales = allLocales.filter((locale) => locale !== 'en-US');
                setSupportedLocales(locales);
            }
            catch (error) {
                console.error('Failed to fetch supported locales:', error);
                setSupportedLocales(undefined);
            }
            finally {
                setIsLoadingSupportedLocales(false);
            }
        };
        fetchSupportedLocales();
    }, [sourceAsset?.id, sourceLocalization]);
    const activityOrigin = useMemo(() => (activityResource ? getActivityOrigin(activityResource) : ActivityOrigin.ORIGINAL), [activityResource]);
    // Memoize early return objects to prevent unnecessary re-renders
    const emptyState = useMemo(() => ({
        sourceId: undefined,
        sourceLink: undefined,
        sourceActivityName: undefined,
        sourceActivity: undefined,
        isLoadingSourceActivity: false,
        sourceActivityVersion: undefined,
        activityOrigin: ActivityOrigin.ORIGINAL,
        isSourceTranslationsAvailable: false,
        isLoadingSupportedLocales: false,
        currentActivity: undefined,
        sourceLocalization: undefined,
        sourceWorkspaceId: undefined,
        sourceWorkspaceName: undefined,
    }), []);
    // Memoize the main return object
    const mainReturnObject = useMemo(() => ({
        sourceId,
        sourceLink,
        sourceActivityName,
        sourceActivity,
        isLoadingSourceActivity,
        sourceActivityVersion,
        activityOrigin,
        isSourceTranslationsAvailable: Array.isArray(supportedLocales) && supportedLocales.length > 0,
        isLoadingSupportedLocales,
        currentActivity: activity,
        sourceLocalization,
        sourceWorkspaceId,
        sourceWorkspaceName,
    }), [
        sourceId,
        sourceLink,
        sourceActivityName,
        sourceActivity,
        isLoadingSourceActivity,
        sourceActivityVersion,
        activityOrigin,
        supportedLocales,
        isLoadingSupportedLocales,
        activity,
        sourceLocalization,
        sourceWorkspaceId,
        sourceWorkspaceName,
    ]);
    // Return early loading state if activity is still loading
    if (isLoadingActivity || !activityId) {
        return emptyState;
    }
    // Return early if no activity found
    if (!activity) {
        return emptyState;
    }
    return mainReturnObject;
};
export function getActivityOrigin(activityResource) {
    if (activityResource.metadata?.clone?.cloned) {
        return ActivityOrigin.CROSS_WORKSPACE_CLONED;
    }
    if (!activityResource.metadata?.source_id) {
        return ActivityOrigin.ORIGINAL;
    }
    // Check if this is a clone (version 1) or a versioned activity (version > 1)
    return parseInt(activityResource.version || '1', 10) === 1 ? ActivityOrigin.CLONED : ActivityOrigin.VERSIONED;
}
//# sourceMappingURL=use_activity_source_status.hook.js.map